/**
 * Hook para gestión de citas CRM
 * @module hooks/useAppointments
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { appointmentService } from '@/services/appointmentService';
import type {
  AppointmentCreate,
  AppointmentUpdate,
  AppointmentListParams,
} from '@/types/appointment.types';

export const useAppointments = (params?: AppointmentListParams) => {
  const queryClient = useQueryClient();

  // Query: Listar citas
  const {
    data: appointments,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['appointments', params],
    queryFn: () => appointmentService.list(params),
    staleTime: 30000, // 30 segundos
  });

  // Mutation: Crear cita
  const createMutation = useMutation({
    mutationFn: (data: AppointmentCreate) => appointmentService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      queryClient.invalidateQueries({ queryKey: ['appointments-calendar'] });
      queryClient.invalidateQueries({ queryKey: ['unified-calendar'] });
    },
  });

  // Mutation: Actualizar cita
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: AppointmentUpdate }) =>
      appointmentService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      queryClient.invalidateQueries({ queryKey: ['appointments-calendar'] });
      queryClient.invalidateQueries({ queryKey: ['unified-calendar'] });
    },
  });

  // Mutation: Eliminar cita
  const deleteMutation = useMutation({
    mutationFn: (id: string) => appointmentService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      queryClient.invalidateQueries({ queryKey: ['appointments-calendar'] });
      queryClient.invalidateQueries({ queryKey: ['unified-calendar'] });
    },
  });

  return {
    appointments,
    isLoading,
    error,
    refetch,
    create: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    update: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    delete: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
  };
};

/**
 * Hook para obtener una cita por ID
 */
export const useAppointment = (id: string | undefined) => {
  return useQuery({
    queryKey: ['appointment', id],
    queryFn: () => {
      if (!id) throw new Error('ID requerido');
      return appointmentService.getById(id);
    },
    enabled: !!id,
    staleTime: 30000,
  });
};

/**
 * Hook para obtener citas del calendario
 */
export const useAppointmentCalendar = (params?: {
  start_date?: string;
  end_date?: string;
  delegation_id?: string;
}) => {
  return useQuery({
    queryKey: ['appointments-calendar', params],
    queryFn: () => appointmentService.getCalendar(params),
    staleTime: 60000, // 1 minuto
  });
};
